/**
 * Pure H OS · Sprint 1 · App entry.
 *
 * Mounts a subtree under /pureh/* isolated from the legacy Hassan OS
 * app. Public: /pureh/login. Everything else requires a mocked session.
 *
 * Sprint 2 additions (non-invasive):
 *   - PlatformProvider (Command Palette, Notifications, Prefs state)
 *   - GlobalOverlays   (Cmd+K palette, notification drawer, shortcut modal)
 *   - New routes       (/notifications, /search, /profile, /settings, /shortcuts)
 */
import { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PureHProvider, usePureH } from "./context/PureHContext";
import { PlatformProvider } from "./context/PlatformContext";
import { DataModeProvider } from "./context/DataModeContext";
import Shell from "./components/Shell";
import GlobalOverlays from "./components/GlobalOverlays";
import Login from "./pages/Login";
import CompanyOverview from "./pages/CompanyOverview";
import Departments from "./pages/Departments";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import Notifications from "./pages/Notifications";
import WorkspaceInbox from "./pages/WorkspaceInbox";
import AuthCallback from "./pages/AuthCallback";
import UsersAccess from "./pages/UsersAccess";
import Licensing from "./pages/Licensing";
import PresentationMode from "./pages/PresentationMode";
import GlobalSearch from "./pages/GlobalSearch";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Shortcuts from "./pages/Shortcuts";
import Decisions from "./pages/Decisions";
import Reports from "./pages/Reports";
import Scorecards from "./pages/Scorecards";
import Briefs from "./pages/Briefs";
import OwnerCockpit from "./pages/OwnerCockpit";
// Phase D · Wave 2
import Timeline from "./pages/Timeline";
import PMO from "./pages/PMO";
import Procurement from "./pages/Procurement";
import Documents from "./pages/Documents";
// Phase D · Wave 3
import OKRs from "./pages/OKRs";
import KnowledgeBase from "./pages/KnowledgeBase";
import Meetings from "./pages/Meetings";
import RiskRegister from "./pages/RiskRegister";
// Phase D · Wave 4
import Warehouses from "./pages/Warehouses";
import UnitEconomics from "./pages/UnitEconomics";
import CostCenters from "./pages/CostCenters";
import StatementOfAccount from "./pages/finance/StatementOfAccount";
import Customer360 from "./pages/Customer360";
import ProductWorkspaceHub from "./pages/ProductWorkspaceHub";
// Legacy Investors mock retired in Foundation Closure Sprint (Iter 124).
// Real module lives at ./pages/investors/InvestorWorkspace.tsx.
import Analytics from "./pages/Analytics";
// Phase D · P0 Interaction Depth
import UnifiedApprovals from "./pages/UnifiedApprovals";
// Phase D · Pre-Production Gap Closure
import ExecutiveInbox from "./pages/ExecutiveInbox";
import FormulaCompare from "./pages/rd/FormulaCompare";
import BudgetVsActual from "./pages/finance/BudgetVsActual";
import CashForecast from "./pages/finance/CashForecast";
import ProfitAndLoss from "./pages/finance/ProfitAndLoss";
import BlindScanPortal from "./pages/BlindScanPortal";
import WeeklyDigest from "./pages/WeeklyDigest";
import DocumentCenter from "./pages/DocumentCenter";
import MarketingLayout from "./pages/marketing/MarketingLayout";
import MarketingOverview from "./pages/marketing/Overview";
import MarketingCalendar from "./pages/marketing/Calendar";
import MarketingPipeline from "./pages/marketing/Pipeline";
import MarketingCreative from "./pages/marketing/CreativeReview";
import MarketingCopy from "./pages/marketing/CopyAssistant";
import MarketingCreators from "./pages/marketing/Creators";
import MarketingChannels from "./pages/marketing/Channels";
import MarketingTasks from "./pages/marketing/Tasks";
import CommerceLayout from "./pages/commerce/CommerceLayout";
import CommerceOverview from "./pages/commerce/Overview";
import CommerceOrders from "./pages/commerce/Orders";
import CommerceProducts from "./pages/commerce/Products";
import CommerceInventory from "./pages/commerce/Inventory";
import CommercePricing from "./pages/commerce/Pricing";
import CommercePromotions from "./pages/commerce/Promotions";
import CommerceSync from "./pages/commerce/ShopifySync";
import CommerceSignals from "./pages/commerce/Signals";
import OperationsLayout from "./pages/operations/OperationsLayout";
import OperationsOverview from "./pages/operations/Overview";
import OperationsBatches from "./pages/operations/Batches";
import OperationsQuality from "./pages/operations/Quality";
import OperationsPackaging from "./pages/operations/Packaging";
import OperationsSuppliers from "./pages/operations/Suppliers"; // legacy mock — route redirects to /pureh/blend/suppliers
import OperationsMovement from "./pages/operations/Movement";
import OperationsFulfillment from "./pages/operations/Fulfillment";
import OperationsRisks from "./pages/operations/Risks";
import FinanceLayout from "./pages/finance/FinanceLayout";
import FinanceOverview from "./pages/finance/Overview";
import FinanceRevenue from "./pages/finance/Revenue";
import FinanceExpenses from "./pages/finance/Expenses";
import FinanceCashFlow from "./pages/finance/CashFlow";
import FinanceBudget from "./pages/finance/Budget";
import FinanceInvoices from "./pages/finance/Invoices";
import FinanceVendorPayments from "./pages/finance/VendorPayments";
import FinanceRisks from "./pages/finance/Risks";
import FinancePage from "./pages/finance/FinancePage";

// Sprint 7 · R&D OS
import RndLayout from "./pages/rd/RndLayout";
import RndLibrary from "./pages/rd/ProductLibrary";
import RndWorkspace from "./pages/rd/ProductWorkspace";
import RndFormulas from "./pages/rd/FormulaManager";
import RndMaterials from "./pages/rd/RawMaterials";
import RndSuppliers from "./pages/rd/RndSuppliers";
import RndLab from "./pages/rd/LabSessions";
import RndSampling from "./pages/rd/Sampling";
import RndBlind from "./pages/rd/BlindTesting";
import RndDecisions from "./pages/rd/Decisions";
import RndTimeline from "./pages/rd/Timeline";

// Sprint 8 · Launch OS
import LaunchLayout from "./pages/launch/LaunchLayout";
import LaunchOverview from "./pages/launch/Overview";
import LaunchControl from "./pages/launch/Control";
import LaunchChecklist from "./pages/launch/Checklist";
import LaunchPreOrders from "./pages/launch/PreOrders";
import LaunchCampaigns from "./pages/launch/Campaigns";
import LaunchSubscribers from "./pages/launch/Subscribers";

// Sprint 9 · AI HQ
import AiLayout from "./pages/ai/AiLayout";
import AiOverview from "./pages/ai/Overview";
import AiTeammates from "./pages/ai/Teammates";
import AiPlaybooks from "./pages/ai/Playbooks";
import AiConversations from "./pages/ai/Conversations";
import AiInsights from "./pages/ai/Insights";
import AiApprovals from "./pages/ai/Approvals";

// Sprint 10 · Workspace
import WorkspaceLayout from "./pages/workspace/WorkspaceLayout";
import WsMyDay from "./pages/workspace/MyDay";
import WsTasks from "./pages/workspace/Tasks";
import WsPeople from "./pages/workspace/People";
import WsFiles from "./pages/workspace/Files";
import WsAudit from "./pages/workspace/AuditLog";

// Sprint 11 · Growth
import GrowthLayout from "./pages/growth/GrowthLayout";
import GrowthOverview from "./pages/growth/Overview";
import GrowthSales from "./pages/growth/SalesPipeline";
import GrowthLoyalty from "./pages/growth/Loyalty";
import GrowthRetention from "./pages/growth/Retention";
import GrowthAttribution from "./pages/growth/Attribution";
// Workstream 01 · Company Setup
import CompanySetupLayout from "./pages/company-setup/CompanySetupLayout";
// Workstream 02 · Product Master
import ProductMasterLayout from "./pages/product-master/ProductMasterLayout";
import KnowledgeImportPage from "./pages/product-master/KnowledgeImportPage";
import BlendModule from "./pages/blend/BlendModule";
import RDLaboratory from "./pages/rd-lab/RDLaboratory";
import RawMaterialsPage from "./pages/rd-lab/RawMaterialsPage";
import InvestorWorkspace from "./pages/investors/InvestorWorkspace";
// Workstream 04 · Inventory · Slice 1
import InventoryLayout from "./pages/inventory/InventoryLayout";
import InvWarehousesList from "./pages/inventory/WarehousesList";
import InvWarehouseDetail from "./pages/inventory/WarehouseDetail";
import InvGRNList from "./pages/inventory/GRNList";
import InvGRNEditor from "./pages/inventory/GRNEditor";
import InvAdjustments from "./pages/inventory/AdjustmentEditor";
import InvStockReport from "./pages/inventory/StockReport";
// Pure H Executive Warm v1 — Design tokens (scoped via .pureh-root)
import _theme from "./theme.css";
// Prevent TS from tree-shaking the CSS side-effect import
void _theme;

function RequireSession({ children }: { children: ReactNode }) {
  const { user, authReady, dataReady } = usePureH();
  const loc = useLocation();
  if (!authReady) {
    // Still checking the session cookie — render a subtle placeholder that
    // matches the app shell so we don't flash the login screen.
    return (
      <div className="pureh-root min-h-screen flex items-center justify-center" data-testid="pureh-boot">
        <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Pure H OS · loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/pureh/login" state={{ from: loc.pathname }} replace />;
  }
  if (!dataReady) {
    return (
      <div className="pureh-root min-h-screen flex items-center justify-center" data-testid="pureh-hydrating">
        <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Loading workspace…</div>
      </div>
    );
  }
  return <>{children}</>;
}

export default function PureHApp() {
  return (
    <PureHProvider>
      <PlatformProvider>
        <DataModeProvider>
        <div className="pureh-root">
          <Routes>
            <Route path="login" element={<Login />} />
            <Route path="auth/callback" element={<AuthCallback />} />
            <Route
              path="*"
              element={
                <RequireSession>
                  <Shell>
                    <GlobalOverlays />
                    <Routes>
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard"     element={<ExecutiveDashboard />} />
                      <Route path="owner"         element={<OwnerCockpit />} />
                      <Route path="company"       element={<Navigate to="/pureh/company-setup" replace />} />
                      <Route path="departments"   element={<Departments />} />
                      <Route path="notifications" element={<Notifications />} />
                      <Route path="search"        element={<GlobalSearch />} />
                      <Route path="profile"       element={<Profile />} />
                      <Route path="settings"      element={<Settings />} />
                      <Route path="admin/users"   element={<UsersAccess />} />
                      <Route path="shortcuts"     element={<Shortcuts />} />
                      <Route path="marketing"     element={<MarketingLayout />}>
                        <Route index               element={<Navigate to="overview" replace />} />
                        <Route path="overview"     element={<MarketingOverview />} />
                        <Route path="calendar"     element={<MarketingCalendar />} />
                        <Route path="pipeline"     element={<MarketingPipeline />} />
                        <Route path="creative"     element={<MarketingCreative />} />
                        <Route path="copy"         element={<MarketingCopy />} />
                        <Route path="creators"     element={<MarketingCreators />} />
                        <Route path="channels"     element={<MarketingChannels />} />
                        <Route path="tasks"        element={<MarketingTasks />} />
                      </Route>
                      <Route path="commerce"      element={<CommerceLayout />}>
                        <Route index               element={<Navigate to="overview" replace />} />
                        <Route path="overview"     element={<CommerceOverview />} />
                        <Route path="orders"       element={<CommerceOrders />} />
                        <Route path="products"     element={<CommerceProducts />} />
                        <Route path="inventory"    element={<CommerceInventory />} />
                        <Route path="pricing"      element={<CommercePricing />} />
                        <Route path="promotions"   element={<CommercePromotions />} />
                        <Route path="sync"         element={<CommerceSync />} />
                        <Route path="signals"      element={<CommerceSignals />} />
                      </Route>
                      <Route path="operations"    element={<OperationsLayout />}>
                        <Route index               element={<Navigate to="overview" replace />} />
                        <Route path="overview"     element={<OperationsOverview />} />
                        <Route path="batches"      element={<OperationsBatches />} />
                        <Route path="quality"      element={<OperationsQuality />} />
                        <Route path="packaging"    element={<OperationsPackaging />} />
                        <Route path="suppliers"    element={<Navigate to="/pureh/blend/suppliers" replace />} />
                        <Route path="movement"     element={<OperationsMovement />} />
                        <Route path="fulfillment"  element={<OperationsFulfillment />} />
                        <Route path="risks"        element={<OperationsRisks />} />
                      </Route>
                      <Route path="finance"       element={<FinanceLayout />}>
                        <Route index               element={<Navigate to="overview" replace />} />
                        <Route path="overview"     element={<FinanceOverview />} />
                        <Route path="revenue"      element={<FinanceRevenue />} />
                        <Route path="expenses"     element={<FinanceExpenses />} />
                        <Route path="cash"         element={<FinanceCashFlow />} />
                        <Route path="budget"       element={<FinanceBudget />} />
                        <Route path="invoices"     element={<FinanceInvoices />} />
                        <Route path="vendors"      element={<FinanceVendorPayments />} />
                        <Route path="business-reality" element={<FinancePage />} />
                        <Route path="pnl"          element={<ProfitAndLoss />} />
                        <Route path="taxes"        element={<Navigate to="/pureh/finance/reports" replace />} />
                        <Route path="cost-centers" element={<Navigate to="/pureh/cost-centers" replace />} />
                        <Route path="reports"      element={<Navigate to="/pureh/reports" replace />} />
                        <Route path="customer-statements" element={<StatementOfAccount />} />
                        <Route path="risks"        element={<FinanceRisks />} />
                      </Route>
                      <Route path="rd"           element={<RndLayout />}>
                        {/* Legacy R&D OS write surfaces retired — redirect to the
                            canonical R&D Laboratory (bound to Product Master). */}
                        <Route index              element={<Navigate to="/pureh/rd-lab" replace />} />
                        <Route path="library"     element={<Navigate to="/pureh/rd-lab" replace />} />
                        <Route path="formulas"    element={<Navigate to="/pureh/rd-lab" replace />} />
                        <Route path="materials"   element={<Navigate to="/pureh/raw-materials" replace />} />
                        <Route path="lab"         element={<Navigate to="/pureh/rd-lab" replace />} />
                        <Route path="workspace"   element={<RndWorkspace />} />
                        <Route path="suppliers"   element={<Navigate to="/pureh/blend/suppliers" replace />} />
                        <Route path="sampling"    element={<RndSampling />} />
                        <Route path="blind"       element={<RndBlind />} />
                        <Route path="decisions"   element={<RndDecisions />} />
                        <Route path="timeline"    element={<RndTimeline />} />
                      </Route>
                      <Route path="launch"      element={<LaunchLayout />}>
                        <Route index              element={<Navigate to="overview" replace />} />
                        <Route path="overview"    element={<LaunchOverview />} />
                        <Route path="control"     element={<LaunchControl />} />
                        <Route path="checklist"   element={<LaunchChecklist />} />
                        <Route path="preorders"   element={<LaunchPreOrders />} />
                        <Route path="campaigns"   element={<LaunchCampaigns />} />
                        <Route path="subscribers" element={<LaunchSubscribers />} />
                      </Route>
                      <Route path="ai"          element={<AiLayout />}>
                        <Route index              element={<Navigate to="overview" replace />} />
                        <Route path="overview"    element={<AiOverview />} />
                        <Route path="teammates"   element={<AiTeammates />} />
                        <Route path="playbooks"   element={<AiPlaybooks />} />
                        <Route path="conversations" element={<AiConversations />} />
                        <Route path="insights"    element={<AiInsights />} />
                        <Route path="approvals"   element={<AiApprovals />} />
                      </Route>
                      <Route path="workspace"   element={<WorkspaceLayout />}>
                        <Route index              element={<Navigate to="myday" replace />} />
                        <Route path="myday"       element={<WsMyDay />} />
                        <Route path="tasks"       element={<WsTasks />} />
                        <Route path="inbox"       element={<WorkspaceInbox />} />
                        <Route path="people"      element={<WsPeople />} />
                        <Route path="files"       element={<WsFiles />} />
                        <Route path="audit"       element={<WsAudit />} />
                      </Route>
                      <Route path="growth"      element={<GrowthLayout />}>
                        <Route index              element={<Navigate to="overview" replace />} />
                        <Route path="overview"    element={<GrowthOverview />} />
                        <Route path="sales"       element={<GrowthSales />} />
                        <Route path="loyalty"     element={<GrowthLoyalty />} />
                        <Route path="retention"   element={<GrowthRetention />} />
                        <Route path="attribution" element={<GrowthAttribution />} />
                      </Route>
                      <Route path="reports"       element={<Reports />} />
                      <Route path="company-setup" element={<CompanySetupLayout />} />
                      <Route path="product-master" element={<ProductMasterLayout />} />
                      <Route path="product-master/knowledge-import" element={<KnowledgeImportPage />} />
                      <Route path="rd-lab" element={<RDLaboratory />} />
                      <Route path="raw-materials" element={<RawMaterialsPage />} />
                      <Route path="blend/*" element={<BlendModule />} />
                      <Route path="investors" element={<InvestorWorkspace />} />
                      {/* Workstream 04 · Inventory · Slice 1 */}
                      <Route path="inventory" element={<InventoryLayout />}>
                        <Route index element={<Navigate to="warehouses" replace />} />
                        <Route path="warehouses" element={<InvWarehousesList />} />
                        <Route path="warehouses/:id" element={<InvWarehouseDetail />} />
                        <Route path="grn" element={<InvGRNList />} />
                        <Route path="grn/new" element={<InvGRNEditor />} />
                        <Route path="grn/:id" element={<InvGRNEditor />} />
                        <Route path="adjustments" element={<InvAdjustments />} />
                        <Route path="report" element={<InvStockReport />} />
                      </Route>
                      <Route path="scorecards"    element={<Scorecards />} />
                      <Route path="briefs"        element={<Briefs />} />
                      <Route path="decisions"     element={<Decisions />} />
                      {/* Phase D · Wave 2 */}
                      <Route path="timeline"      element={<Timeline />} />
                      <Route path="pmo"           element={<PMO />} />
                      <Route path="procurement"   element={<Procurement />} />
                      <Route path="documents"     element={<Documents />} />
                      {/* Phase D · Wave 3 */}
                      <Route path="okrs"          element={<OKRs />} />
                      <Route path="knowledge"     element={<KnowledgeBase />} />
                      <Route path="meetings"      element={<Meetings />} />
                      <Route path="risks"         element={<RiskRegister />} />
                      {/* Phase D · Wave 4 */}
                      <Route path="warehouses"    element={<Warehouses />} />
                      <Route path="unit-economics"element={<UnitEconomics />} />
                      <Route path="cost-centers"  element={<CostCenters />} />
                      <Route path="customers"     element={<Customer360 />} />
                      <Route path="products-hub"  element={<ProductWorkspaceHub />} />
                      <Route path="analytics"     element={<Analytics />} />
                      {/* Phase D · P0 · Unified Approvals canonical queue */}
                      <Route path="approvals"     element={<UnifiedApprovals />} />
                      {/* Pre-Production Gap Closure */}
                      <Route path="inbox"                    element={<Navigate to="/pureh/workspace/inbox" replace />} />
                      <Route path="rd/formulas/compare"      element={<FormulaCompare />} />
                      <Route path="finance/budget-vs-actual" element={<BudgetVsActual />} />
                      <Route path="finance/cash-forecast"    element={<CashForecast />} />
                      <Route path="finance/pnl"              element={<ProfitAndLoss />} />
                      <Route path="reports/weekly-digest"    element={<WeeklyDigest />} />
                      <Route path="documents/center"         element={<DocumentCenter />} />
                      <Route path="blind/scan/:code"         element={<BlindScanPortal />} />
                      {/* Wave A · IA amendment · new lightweight redirects */}
                      <Route path="licensing"     element={<Licensing />} />
                      <Route path="presentation"  element={<PresentationMode />} />
                      {/* Backwards-compat redirect for the old /organization URL */}
                      <Route path="organization"  element={<Navigate to="/pureh/company-setup" replace />} />
                      <Route path="*"             element={<Navigate to="/pureh/dashboard" replace />} />
                    </Routes>
                  </Shell>
                </RequireSession>
              }
            />
          </Routes>
        </div>
        </DataModeProvider>
      </PlatformProvider>
    </PureHProvider>
  );
}
